import { useState, useRef } from "react"
import Papa from "papaparse"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { useToast } from "@/hooks/use-toast"
import { Upload, X, AlertCircle, CheckCircle, Download } from "lucide-react"
import { useCreateEventGuest } from "@/hooks/useEventGuests"

interface CSVParseResult {
  data: Record<string, string>[]
  meta: { fields?: string[] }
}

interface CSVParseError {
  message: string
}

interface GuestRow {
  name: string
  handle?: string
  contact_info?: string
  category: string
  rsvp_status: string
  errors?: string[]
}

interface ImportGuestsModalProps {
  isOpen: boolean
  onClose: () => void
  eventId: string
}

const VALID_CATEGORIES = ['Influencer', 'Media', 'VIP', 'Others']
const VALID_RSVP_STATUS = ['Confirmed', 'Pending', 'Invited', 'Declined', 'Suggested']

export function ImportGuestsModal({ isOpen, onClose, eventId }: ImportGuestsModalProps) {
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [parsedData, setParsedData] = useState<GuestRow[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { toast } = useToast()
  const createGuest = useCreateEventGuest()

  const downloadSampleCSV = () => {
    const sampleData = [
      ['name', 'handle', 'contact_info', 'category', 'rsvp_status'],
      ['Alice Smith', '@alicesmith', 'alice@example.com', 'Influencer', 'Confirmed'],
      ['Bob Jones', '@bobjones', 'bob@example.com', 'Media', 'Pending'],
      ['Charlie Lee', '@charlielee', 'charlie@example.com', 'VIP', 'Invited']
    ]
    
    const csvContent = sampleData.map(row => row.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', 'guestlist-sample.csv')
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file && file.type === 'text/csv') {
      setCsvFile(file)
      parseCSV(file)
    } else {
      toast({
        title: "Invalid file type",
        description: "Please select a CSV file",
        variant: "destructive"
      })
    }
  }

  const parseCSV = (file: File) => {
    setIsProcessing(true)

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header: string) => header.trim().toLowerCase(),
      complete: (results: CSVParseResult) => {
        try {
          const rows = results.data
          const headers = results.meta.fields ?? []

          if (rows.length < 1) {
            toast({
              title: "Invalid CSV",
              description: "CSV must contain at least a header and one data row",
              variant: "destructive"
            })
            return
          }

          const requiredHeaders = ['name', 'category', 'rsvp_status']
          const missingHeaders = requiredHeaders.filter(h => !headers.includes(h))

          if (missingHeaders.length > 0) {
            toast({
              title: "Invalid CSV format",
              description: `Missing required columns: ${missingHeaders.join(', ')}`,
              variant: "destructive"
            })
            return
          }

          const data: GuestRow[] = rows.map((rawRow) => {
            const row: GuestRow = {
              name: '',
              category: 'Others',
              rsvp_status: 'Suggested',
              errors: []
            }

            headers.forEach((header: string) => {
              const value = (rawRow[header] ?? '').trim().replace(/"/g, '')
              switch (header) {
                case 'name':
                  row.name = value
                  break
                case 'handle':
                  row.handle = value.startsWith('@') ? value : value ? `@${value}` : undefined
                  break
                case 'contact_info':
                  row.contact_info = value
                  break
                case 'category':
                  row.category = VALID_CATEGORIES.includes(value) ? value : 'Others'
                  if (!VALID_CATEGORIES.includes(value) && value) {
                    row.errors?.push(`Invalid category: ${value}`)
                  }
                  break
                case 'rsvp_status':
                  row.rsvp_status = VALID_RSVP_STATUS.includes(value) ? value : 'Suggested'
                  if (!VALID_RSVP_STATUS.includes(value) && value) {
                    row.errors?.push(`Invalid RSVP status: ${value}`)
                  }
                  break
              }
            })

            // Validation
            if (!row.name) {
              row.errors?.push('Name is required')
            }

            return row
          })

          setParsedData(data)
          setShowPreview(true)
        } finally {
          setIsProcessing(false)
        }
      },
      error: (error: CSVParseError) => {
        console.error('CSV parsing error:', error)
        toast({
          title: "Error parsing CSV",
          description: "Please check your CSV format and try again",
          variant: "destructive"
        })
        setIsProcessing(false)
      }
    })
  }

  const handleImport = async () => {
    if (!parsedData.length) return

    setIsProcessing(true)
    let successCount = 0
    let errorCount = 0

    try {
      for (const row of parsedData) {
        if (row.errors?.length || !row.name) {
          errorCount++
          continue
        }

        // Determine if contact_info is email or phone
        const isEmail = row.contact_info?.includes('@')
        
        try {
          await createGuest.mutateAsync({
            event_id: eventId,
            full_name: row.name,
            handle: row.handle || null,
            email: isEmail ? row.contact_info : null,
            phone: !isEmail ? row.contact_info : null,
            guest_type: row.category,
            rsvp_status: row.rsvp_status,
            category_tags: [],
            notes: null
          })
          successCount++
        } catch (error) {
          console.error('Error creating guest:', error)
          errorCount++
        }
      }

      toast({
        title: "Import completed",
        description: `Successfully imported ${successCount} guests. ${errorCount > 0 ? `${errorCount} failed.` : ''}`,
        variant: successCount > 0 ? "default" : "destructive"
      })

      if (successCount > 0) {
        handleClose()
      }
    } catch (error) {
      console.error('Import error:', error)
      toast({
        title: "Import failed",
        description: "An error occurred during import",
        variant: "destructive"
      })
    } finally {
      setIsProcessing(false)
    }
  }

  const handleClose = () => {
    setCsvFile(null)
    setParsedData([])
    setShowPreview(false)
    setIsProcessing(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    onClose()
  }

  const validRows = parsedData.filter(row => !row.errors?.length && row.name)
  const invalidRows = parsedData.filter(row => row.errors?.length || !row.name)

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-gray-900">Import Guests via CSV</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {!showPreview ? (
            <>
              {/* File Upload */}
              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <div className="space-y-2">
                    <h3 className="text-lg font-medium text-gray-900">Upload CSV File</h3>
                    <p className="text-gray-600">Select a CSV file containing guest information</p>
                  </div>
                  <Input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="mt-4"
                  />
                  
                  {/* Download Sample CSV Button */}
                  <div className="mt-4">
                    <Button
                      variant="outline"
                      onClick={downloadSampleCSV}
                      className="text-purple-600 border-purple-600 hover:bg-purple-50"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download Sample CSV
                    </Button>
                  </div>
                </div>
              </div>

              {/* Example Format */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Expected CSV Format:</h4>
                <div className="bg-white rounded border p-3 font-mono text-sm">
                  <div className="text-gray-600">name,handle,contact_info,category,rsvp_status</div>
                  <div>John Doe,@johndoe,john@example.com,Influencer,Confirmed</div>
                  <div>Jane Smith,,jane@example.com,Media,Pending</div>
                </div>
                <div className="mt-3 space-y-1 text-sm text-gray-600">
                  <p><strong>Required fields:</strong> name, category, rsvp_status</p>
                  <p><strong>Categories:</strong> {VALID_CATEGORIES.join(', ')}</p>
                  <p><strong>RSVP Status:</strong> {VALID_RSVP_STATUS.join(', ')}</p>
                  <p><strong>Contact Info:</strong> Can be email or phone number</p>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Preview Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Import Preview</h3>
                  <Button variant="outline" onClick={() => setShowPreview(false)}>
                    <X className="h-4 w-4 mr-2" />
                    Back to Upload
                  </Button>
                </div>

                {/* Summary */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                      <span className="font-medium text-green-900">Valid Rows: {validRows.length}</span>
                    </div>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                      <span className="font-medium text-red-900">Invalid Rows: {invalidRows.length}</span>
                    </div>
                  </div>
                </div>

                {/* Preview Table */}
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Status</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Handle</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>RSVP Status</TableHead>
                        <TableHead>Errors</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {parsedData.slice(0, 10).map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            {row.errors?.length || !row.name ? (
                              <Badge variant="destructive">Invalid</Badge>
                            ) : (
                              <Badge variant="default">Valid</Badge>
                            )}
                          </TableCell>
                          <TableCell className="font-medium">{row.name || '-'}</TableCell>
                          <TableCell>{row.handle || '-'}</TableCell>
                          <TableCell>{row.contact_info || '-'}</TableCell>
                          <TableCell>{row.category}</TableCell>
                          <TableCell>{row.rsvp_status}</TableCell>
                          <TableCell className="text-red-600 text-sm">
                            {row.errors?.join(', ') || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {parsedData.length > 10 && (
                    <div className="p-3 text-center text-sm text-gray-600 border-t">
                      Showing first 10 rows of {parsedData.length} total rows
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Footer Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={handleClose} disabled={isProcessing}>
              Cancel
            </Button>
            {showPreview && (
              <Button 
                onClick={handleImport} 
                disabled={isProcessing || validRows.length === 0}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {isProcessing ? 'Importing...' : `Import ${validRows.length} Guests`}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
